import { createApiRef, DiscoveryApi, IdentityApi } from '@backstage/core-plugin-api';

export type OwnedRepo = {
  projectSlug: string;
  repoUrl: string;
  teamSlug: string;
  groupRef: string;
  githubData?: {
    fromCache: boolean;
    stale: boolean;
    fetchedAt: string;
  };
};

export type RegisteredRepo = OwnedRepo & {
  entities: Array<{
    entityRef: string;
    kind: string;
    name: string;
    namespace: string;
    owner?: string;
    repoLinkSource?: string | null;
  }>;
};

export type RepoDiscoveryResponse = {
  inputGroups: string[];
  githubTeams: Array<{ groupRef: string; teamSlug: string }>;
  ownedCount: number;

  pendingCount: number;
  pending: OwnedRepo[];

  registeredCount: number;
  registered: RegisteredRepo[];

  githubDataStale?: boolean;
  warnings?: string[];

  rateLimit?: {
    remaining?: number;
    resetEpochSeconds?: number;
    retryAfterSeconds?: number;
  };

  unmappedEntityCount?: number;
  unmappedEntities?: Array<{ entityRef: string; reason: string }>;

  _fromUserCache?: boolean;
  note?: string;
};

export interface RepoDiscoveryApi {
  getRegistrationStatus(options?: { forceRefresh?: boolean }): Promise<RepoDiscoveryResponse>;
}

export const repoDiscoveryApiRef = createApiRef<RepoDiscoveryApi>({
  id: 'plugin.gitrepo-discovery.service',
});

export class RepoDiscoveryApiClient implements RepoDiscoveryApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly identityApi: IdentityApi,
  ) {}

  async getRegistrationStatus(options?: { forceRefresh?: boolean }): Promise<RepoDiscoveryResponse> {
    const baseUrl = await this.discoveryApi.getBaseUrl('gitrepo-discovery');
    const { token } = await this.identityApi.getCredentials();

    const url = options?.forceRefresh
      ? `${baseUrl}/unregistered?refresh=true`
      : `${baseUrl}/unregistered`;

    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const msg = data?.error?.message ?? data?.error ?? res.statusText;
      throw new Error(msg);
    }

    return data as RepoDiscoveryResponse;
  }
}